<template>
  <article class="ws-cli-row">
    <div class="ws-cli-row__avatar" aria-hidden="true">
      <span class="ws-cli-row__avatar-inner">{{ initials }}</span>
    </div>

    <div class="ws-cli-row__main min-w-0">
      <button type="button" class="ws-cli-row__open" @click="$emit('open', cliente)">
        <div class="ws-cli-row__identity min-w-0">
          <h3 class="ws-cli-row__title">{{ cliente.nome }}</h3>
          <span v-if="cliente.cadastroCompleto === false" class="ws-cli-badge ws-cli-badge--warn">
            {{ t('workspaceClientes.incomplete') }}
          </span>
        </div>

        <div class="ws-cli-row__contacts">
          <span v-if="cliente.email" class="ws-cli-contact">
            <svg class="ws-cli-contact__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            <span class="ws-cli-contact__text">{{ cliente.email }}</span>
          </span>
          <span v-if="cliente.telefoneWhatsapp" class="ws-cli-contact">
            <svg class="ws-cli-contact__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
            </svg>
            <span class="ws-cli-contact__text">{{ formatPhone(cliente.telefoneWhatsapp) }}</span>
          </span>
          <span v-if="cliente.cpf" class="ws-cli-contact">
            <svg class="ws-cli-contact__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2v-5M14 4h6m0 0v6m0-6L10 14"/>
            </svg>
            <span class="ws-cli-contact__text">{{ cliente.cpf }}</span>
          </span>
          <span v-if="!cliente.email && !cliente.telefoneWhatsapp && !cliente.cpf" class="ws-cli-contact ws-cli-contact--muted">
            {{ t('workspaceClientes.noDocument') }}
          </span>
        </div>

        <div v-if="tagList.length" class="ws-cli-row__tags">
          <WorkspaceTagChip
            v-for="tag in tagList"
            :key="tag.id"
            :tag="tag"
            selected
          />
        </div>
      </button>
    </div>

    <div class="ws-cli-row__aside">
      <div v-if="hasIndicators" class="ws-cli-row__status">
        <div
          v-if="cliente.proximoAgendamento"
          class="ws-cli-status ws-cli-status--agenda"
          :title="agendamentoTitle"
        >
          <span class="ws-cli-status__icon" aria-hidden="true">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
          </span>
          <span class="ws-cli-status__copy">
            <span class="ws-cli-status__label">{{ t('workspaceClientes.row.agendaShort') }}</span>
            <span class="ws-cli-status__value">{{ agendamentoLabel }}</span>
          </span>
        </div>

        <div
          v-if="cliente.recebiveisAtrasadosCount > 0"
          class="ws-cli-status ws-cli-status--overdue"
          :title="t('workspaceClientes.row.overdue', { count: cliente.recebiveisAtrasadosCount })"
        >
          <span class="ws-cli-status__icon" aria-hidden="true">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
          </span>
          <span class="ws-cli-status__copy">
            <span class="ws-cli-status__label">{{ t('workspaceClientes.row.overdueShort') }}</span>
            <span class="ws-cli-status__value">{{ cliente.recebiveisAtrasadosCount }}</span>
          </span>
        </div>

        <div
          v-if="cliente.recebiveisVencendo7DiasCount > 0"
          class="ws-cli-status ws-cli-status--due"
          :title="t('workspaceClientes.row.dueSoon', { count: cliente.recebiveisVencendo7DiasCount })"
        >
          <span class="ws-cli-status__icon" aria-hidden="true">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </span>
          <span class="ws-cli-status__copy">
            <span class="ws-cli-status__label">{{ t('workspaceClientes.row.dueSoonShort') }}</span>
            <span class="ws-cli-status__value">{{ cliente.recebiveisVencendo7DiasCount }}</span>
          </span>
        </div>
      </div>

      <div class="ws-cli-row__toolbar">
        <div class="ws-cli-row__actions" @click.stop>
          <button
            type="button"
            class="ws-cli-row__action ws-cli-row__action--historico"
            @click="$emit('open-historico', cliente)"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
            <span>{{ t('workspaceClientes.actions.historico') }}</span>
          </button>

          <button
            v-if="showWhatsApp"
            type="button"
            class="ws-cli-row__action ws-cli-row__action--whatsapp"
            :disabled="!hasWhatsappPhone"
            :title="whatsappTitle"
            @click="$emit('open-whatsapp', cliente)"
          >
            <svg fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            <span>{{ t('workspaceClientes.actions.whatsapp') }}</span>
          </button>

          <button
            type="button"
            class="ws-cli-row__action ws-cli-row__action--agendamento"
            @click="$emit('open-agendamento', cliente)"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
            <span>{{ t('workspaceClientes.actions.agendamento') }}</span>
          </button>

          <button
            type="button"
            class="ws-cli-row__action ws-cli-row__action--financeiro"
            @click="$emit('open-financeiro', cliente)"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            <span>{{ t('workspaceClientes.actions.financeiro') }}</span>
          </button>

          <button
            type="button"
            class="ws-cli-row__action ws-cli-row__action--anexos"
            @click="$emit('open-anexos', cliente)"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/></svg>
            <span>{{ t('workspaceClientes.actions.anexos') }}</span>
          </button>
        </div>

        <button type="button" class="ws-cli-row__chevron" :title="t('workspaceClientes.actions.abrir')" @click="$emit('open', cliente)">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
    </div>
  </article>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { maskPhoneBr } from '@/utils/inputMasks'
import WorkspaceTagChip from '@/components/cliente/WorkspaceTagChip.vue'

const props = defineProps({
  cliente: { type: Object, required: true },
  tags: { type: Array, default: () => [] },
  showWhatsApp: { type: Boolean, default: false }
})

defineEmits([
  'open',
  'open-historico',
  'open-whatsapp',
  'open-agendamento',
  'open-financeiro',
  'open-anexos'
])

const { t, locale } = useI18n()

const parseTagIds = (cliente) => {
  try {
    const raw = cliente.tags
    const arr = typeof raw === 'string' ? JSON.parse(raw || '[]') : (raw || [])
    return Array.isArray(arr) ? arr.map(Number) : []
  } catch {
    return []
  }
}

const tagList = computed(() => {
  const ids = parseTagIds(props.cliente)
  return props.tags.filter(tag => ids.includes(Number(tag.id)))
})

const initials = computed(() => {
  const name = (props.cliente.nome || '').trim()
  if (!name) return '?'
  const parts = name.split(/\s+/).filter(Boolean).slice(0, 2)
  return parts.map(p => p.charAt(0).toUpperCase()).join('')
})

const hasIndicators = computed(() =>
  props.cliente.proximoAgendamento ||
  props.cliente.recebiveisAtrasadosCount > 0 ||
  props.cliente.recebiveisVencendo7DiasCount > 0
)

const hasWhatsappPhone = computed(() => !!String(props.cliente.telefoneWhatsapp || '').trim())

const whatsappTitle = computed(() => {
  if (!hasWhatsappPhone.value) return t('workspaceClienteModal.whatsapp.phoneMissing')
  return t('workspaceClientes.actions.whatsapp')
})

const formatPhone = (value) => {
  if (!value) return ''
  const digits = String(value).replace(/\D/g, '')
  if (digits.length >= 10) return maskPhoneBr(digits)
  return value
}

const dateLocale = computed(() => {
  if (locale.value === 'en') return 'en-US'
  if (locale.value === 'es') return 'es'
  return 'pt-BR'
})

const agendamentoLabel = computed(() => {
  const ag = props.cliente.proximoAgendamento
  if (!ag?.data) return ''
  const hora = ag.horario ? String(ag.horario).slice(0, 5) : ''
  try {
    const [y, m, d] = String(ag.data).slice(0, 10).split('-').map(Number)
    const dt = new Date(y, m - 1, d)
    const dateStr = dt.toLocaleDateString(dateLocale.value, { day: '2-digit', month: '2-digit' })
    return hora ? `${dateStr} · ${hora}` : dateStr
  } catch {
    return hora ? `${ag.data} · ${hora}` : ag.data
  }
})

const agendamentoTitle = computed(() => {
  if (!props.cliente.proximoAgendamento) return ''
  return t('workspaceClientes.row.agendamento', { when: agendamentoLabel.value })
})
</script>
